import Button from '@srm/ui/button';
import SupportBar from '@molecules/SupportBar';
import { useConsentArming } from '@molecules/SupportBar/useConsentArming';
import type { ISupportRequestItem, SupportRequestAction } from './useSupportRequests';
import { formatRequestCountdown, supportRequestDetails, supportRequestTitle } from './supportRequestText';

export interface ISupportRequestBarProps {
    request: ISupportRequestItem;
    /** Текущее время для обратного отсчёта согласия */
    now: number;
    /** Идёт ответ на этот запрос */
    busy?: SupportRequestAction;
    /** «Показать экран» / «Разрешить». Для приглашения вызывается прямо в нажатии: это жест для выбора вкладки */
    onAccept: () => void;
    /** «Не сейчас» / «Отклонить» */
    onDecline: () => void;
}

/**
 * Плашка запроса поддержки в зоне `.notes` (IMPLEMENTATION.md §18): приглашение к показу экрана (тон просмотра)
 * или согласие на вход от имени (тон управления) с обратным отсчётом. Текст переносится: обращение и причина —
 * второй строкой. Согласие объявляется диктору сразу (`role="alert"`), приглашение — через live-область провайдера.
 * «Показать экран» и «Разрешить» принимают нажатие не сразу после появления плашки (`useConsentArming`).
 */
const SupportRequestBar = ({ request, now, busy, onAccept, onDecline }: ISupportRequestBarProps) => {
    const invite = request.kind === 'invite';
    const details = supportRequestDetails(request);
    const countdown = invite ? '' : formatRequestCountdown(request.deadline, now);
    const arming = useConsentArming(request.id);

    return (
        <SupportBar
            tone={invite ? 'view' : 'control'}
            person={request.actorName}
            title={supportRequestTitle(request)}
            description={details || undefined}
            wrap
            live={invite ? false : 'alert'}
            aside={countdown ? <span aria-label={`Осталось ${countdown}`}>осталось {countdown}</span> : undefined}
            label={invite ? 'Приглашение к показу экрана' : 'Запрос доступа к учётной записи'}
            data-testid={`support-request-${request.kind}`}
            actions={
                <>
                    <Button
                        size="sm"
                        loading={busy === 'accept'}
                        disabled={!!busy}
                        ref={arming.ref}
                        data-arming={!arming.armed || undefined}
                        onClick={arming.guard(onAccept)}
                    >
                        {invite ? 'Показать экран' : 'Разрешить'}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline-neutral"
                        loading={busy === 'decline'}
                        disabled={!!busy}
                        onClick={onDecline}
                    >
                        {invite ? 'Не сейчас' : 'Отклонить'}
                    </Button>
                </>
            }
        />
    );
};

export default SupportRequestBar;
